import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { MdSnackBar } from '@angular/material';
import { BsModalRef } from 'ngx-bootstrap/modal/modal-options.class';
import { ToasterService } from 'angular2-toaster';
import swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import * as moment from 'moment';
import { ReportMailConfigService } from '../../../services/index';

@Component({
    selector: 'add-edit-report-mail-config',
    templateUrl: './addEditReportMailConfig.component.html'
})
export class AddEditReportMailConfigComponent implements OnInit {
    public title: string;
    public rowdata: any = {};
    isLoading: boolean = false;
    reportNameList: string[] = [];

    employeeEmailList: any[] = [];
    selectedEmail: string = '';

    isExcelUpload: boolean = false;
    @ViewChild('fileUpload') fileUpload;
    excelData: any;
    rawFileName: string = '';
    uploadList: any[] = [];

    @Output() saveEvent = new EventEmitter();

    constructor(
        private service: ReportMailConfigService,
        public snackBar: MdSnackBar,
        public bsModalRef: BsModalRef,
        public toasterService: ToasterService
    ) { }

    ngOnInit(): void {
        this.service.getReportNames().then((res: any) => {
            if (res && res.success) { this.reportNameList = res.data || []; }
        });

        this.service.getActiveEmployeeEmails().then((res: any) => {
            if (res && res.data) {
                this.employeeEmailList = res.data.filter((e: any) => e.status === 'Active');
            }
            if (this.rowdata.mode === 'Edit' && this.rowdata.report_name) {
                this.service.getMailsByReport(this.rowdata.report_name).then((r: any) => {
                    if (r && r.success && r.data && r.data.length > 0) {
                        this.selectedEmail = r.data[0].mail_id;
                        this.rowdata.status = r.data[0].is_active == 1 ? 'Active' : 'InActive';
                    }
                });
            } else {
                this.rowdata.status = 'Active';
                this.selectedEmail = '';
            }
        });
    }

    onUploadModeChange(): void {
        if (!this.isExcelUpload) { this.clearExcelData(); }
    }

    downloadSampleFile(): void {
        const ws = XLSX.utils.aoa_to_sheet([
            ['REPORT NAME', 'MAIL ID', 'STATUS'],
            ['Overall_SOA', 'example@email.com', 'Active']
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, 'Report_Mail_Config_Sample_' + moment().format('DD-MM-YYYY_HH-mm') + '.xlsx');
    }

    openFileWindow(): void {
        this.fileUpload.nativeElement.click();
    }

    uploadFile(sheet): void {
        if (sheet.target.files.length === 0) { return; }
        const file = sheet.target.files[0];
        const reader = new FileReader();
        reader.onload = (event) => {
            const data = reader.result;
            const workBook = XLSX.read(data, { type: 'binary', cellDates: true });
            const jsonData = workBook.SheetNames.reduce((initial, name) => {
                initial[name] = XLSX.utils.sheet_to_json(workBook.Sheets[name]);
                return initial;
            }, {});
            if (Object.keys(jsonData).length === 1) {
                this.excelData = JSON.stringify(jsonData);
                this.uploadList = (jsonData as any).Sheet1;
                this.rawFileName = file.name;
                this.validateExcelData();
            } else {
                this.clearExcelData();
                sheet.srcElement.value = null;
                swal({ title: 'ERROR', text: 'Please upload a file with only one sheet', type: 'error' });
            }
        };
        reader.readAsBinaryString(file);
        this.fileUpload.nativeElement.value = '';
    }

    validateExcelData(): void {
        const required = ['REPORT NAME', 'MAIL ID', 'STATUS'];
        const errors = [];
        if (this.uploadList.length === 0) { errors.push('Excel file is empty'); }
        const firstRow = this.uploadList[0] || {};
        for (let col of required) {
            if (!(col in firstRow)) { errors.push(`Missing required column: ${col}`); }
        }
        if (errors.length > 0) {
            swal({ title: 'Validation Errors', html: this.buildErrorHtml(errors), type: 'error' });
            this.clearExcelData();
        }
    }

    buildErrorHtml(errors: string[]): string {
        let html = `<div style="max-height:300px;overflow-y:auto;text-align:left;border:1px solid #ddd;border-radius:4px;padding:6px 10px;">`;
        errors.forEach((err, i) => {
            html += `<div style="padding:5px 0;border-bottom:1px solid #eee;color:#dc3545;font-size:14px;">${i + 1}. ${err}</div>`;
        });
        html += `</div>`;
        return html;
    }

    clearExcelData(): void {
        this.excelData = '';
        this.rawFileName = '';
        this.uploadList = [];
    }

    isFormInvalid(): boolean {
        if (this.isExcelUpload) { return !this.excelData || this.uploadList.length === 0; }
        return !this.rowdata.report_name || !this.selectedEmail || !this.rowdata.status;
    }

    onSave(): void {
        if (this.isExcelUpload) {
            this.saveExcelFile();
        } else {
            this.saveManual();
        }
    }

    saveManual(): void {
        if (!this.rowdata.report_name) {
            this.toasterService.pop({ type: 'error', title: 'ERROR', body: 'Please select a Report Name' });
            return;
        }
        if (!this.selectedEmail) {
            this.toasterService.pop({ type: 'error', title: 'ERROR', body: 'Please select a Mail ID' });
            return;
        }
        let that = this;
        this.isLoading = true;
        const payload = {
            mode: this.rowdata.mode,
            report_name: this.rowdata.report_name,
            mail_ids: this.selectedEmail,
            is_active: this.rowdata.status === 'Active' ? 1 : 0
        };
        this.service.addUpdate(payload).then(function(res: any) {
            that.isLoading = false;
            if (res.success) {
                swal('SUCCESS', res.message, 'success');
                that.bsModalRef.hide();
                that.saveEvent.emit();
            } else {
                that.toasterService.pop({ type: 'error', title: 'ERROR', body: res.message });
            }
        });
    }

    saveExcelFile(): void {
        if (!this.excelData || this.uploadList.length === 0) {
            swal({ title: 'ERROR', text: 'Please upload an Excel file first', type: 'error' });
            return;
        }
        const self = this;
        swal({
            title: 'Confirm Upload',
            text: `Are you sure you want to upload ${this.uploadList.length} record(s)?`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Upload',
            cancelButtonText: 'Cancel'
        }).then(function() {
            self.isLoading = true;
            self.service.uploadExcel({ data: self.excelData }).then(function(res: any) {
                self.isLoading = false;
                if (res.success) {
                    self.clearExcelData();
                    swal({ title: 'Success', html: self.buildResultHtml(res.message, res.errors), type: 'success' });
                    self.bsModalRef.hide();
                    self.saveEvent.emit();
                } else {
                    swal({ title: 'ERROR', html: self.buildResultHtml(res.message, res.errors), type: 'error' });
                }
            }).catch(() => {
                self.isLoading = false;
                swal({ title: 'ERROR', text: 'An error occurred while uploading', type: 'error' });
            });
        }, function() {});
    }

    buildResultHtml(message: string, errors: string[]): string {
        let html = `<p style="margin-bottom:8px;">${message}</p>`;
        if (errors && errors.length > 0) {
            html += `<div style="max-height:300px;overflow-y:auto;text-align:left;border:1px solid #ddd;border-radius:4px;padding:6px 10px;">`;
            errors.forEach((err, i) => {
                html += `<div style="padding:5px 0;border-bottom:1px solid #eee;color:#dc3545;font-size:15px;font-weight:bold;">${i + 1}. ${err}</div>`;
            });
            html += `</div>`;
        }
        return html;
    }
}



// WEBPACK FOOTER //
// /Applications/MAMP/htdocs/crm-soundsgood/src/app/components/master/reportMailConfig/addEditReportMailConfig.component.ts